/**
 * workflowStore — two complementary surfaces:
 *
 *   1. Persistence helpers (ported verbatim from v1). Records every
 *      processed email/workflow pair in localStorage so the
 *      "Already processed" warning survives task-pane reloads.
 *
 *   2. Reactive in-memory store keyed by instance_id (NEW per Agent E
 *      brief). Holds whatever live phase data SignalR pushes for a
 *      currently-running workflow. Vanilla pub/sub — no Zustand. Used
 *      by useWorkflowStatus to fan SignalR pushes out to React state.
 *
 *   { getState, setState, subscribe }    — for the reactive store
 *   { isEmailAlreadyProcessed, addWorkflow, ... }  — persistence
 */

import type { WorkflowState } from "../types/endorsement";

// ---------------------------------------------------------------------------
// (1) Persistence — verbatim port of v1's localStorage helpers.
// ---------------------------------------------------------------------------

const KEY = "hsip_endorsement_workflows";

export interface StoredWorkflow {
  workflowId: string;
  messageId: string;
  subject: string;
  sender: string;
  startedAt: string;
  status: WorkflowState;
  lastUpdated: string;
}

function load(): StoredWorkflow[] {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? "[]") as StoredWorkflow[];
  } catch {
    return [];
  }
}

function save(ws: StoredWorkflow[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(ws.slice(-100)));
  } catch {
    // localStorage unavailable (private browsing, quota, security policy)
    // — degrade silently. Matches v1.
  }
}

export const isEmailAlreadyProcessed = (messageId: string): StoredWorkflow | null =>
  load().find((w) => w.messageId === messageId) ?? null;

export const addWorkflow = (w: StoredWorkflow): void => save([...load(), w]);

export const updateWorkflowStatus = (workflowId: string, status: WorkflowState): void => {
  const ws = load();
  const w = ws.find((x) => x.workflowId === workflowId);
  if (w) {
    w.status = status;
    w.lastUpdated = new Date().toISOString();
    save(ws);
  }
};

export const getRecentWorkflows = (limit = 10): StoredWorkflow[] =>
  load().slice(-limit).reverse();

export const clearAllWorkflows = (): void => {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
};

// ---------------------------------------------------------------------------
// (2) Reactive in-memory store — keyed by instance_id.
// ---------------------------------------------------------------------------

/** Append-only events from SignalR are folded into this snapshot. */
export interface ActiveInstanceState {
  /** Substrate instance_id (UUID). */
  instance_id: string;
  /** Catalog workflow id, e.g. `carrier_mortgagee_update`. */
  workflow_id?: string;
  /** Substrate status — running | paused_hitl | completed | failed | … */
  status?: string;
  /** Currently executing phase id. */
  current_phase?: string | null;
  /** Last event timestamp. */
  updated_at?: string;
  /** Last error_message if any. */
  error_message?: string | null;
  /** Free-form metadata accumulated from event payloads. */
  data?: Record<string, unknown>;
}

type Subscriber = (state: Map<string, ActiveInstanceState>) => void;

const _state = new Map<string, ActiveInstanceState>();
const _subscribers = new Set<Subscriber>();

function _notify(): void {
  // Pass a fresh Map so subscribers don't accidentally mutate the source.
  const snapshot = new Map(_state);
  _subscribers.forEach((fn) => fn(snapshot));
}

/**
 * Reactive store API. Vanilla pub/sub semantics:
 *
 *   const off = workflowStore.subscribe((state) => render(state));
 *   workflowStore.setState(id, { status: "running" });
 *   off();
 */
export const workflowStore = {
  getState(instanceId?: string): ActiveInstanceState | undefined {
    if (!instanceId) return undefined;
    return _state.get(instanceId);
  },

  /**
   * Get the entire snapshot (defensive copy — callers may treat as
   * immutable).
   */
  getAll(): Map<string, ActiveInstanceState> {
    return new Map(_state);
  },

  /**
   * Merge a partial state for a single instance. Pass `null` to delete.
   */
  setState(
    instanceId: string,
    patch: Partial<ActiveInstanceState> | null,
  ): void {
    if (patch === null) {
      _state.delete(instanceId);
      _notify();
      return;
    }
    const existing = _state.get(instanceId) ?? { instance_id: instanceId };
    const next: ActiveInstanceState = {
      ...existing,
      ...patch,
      // Always keep instance_id pinned to the key.
      instance_id: instanceId,
      data: { ...(existing.data ?? {}), ...(patch.data ?? {}) },
    };
    _state.set(instanceId, next);
    _notify();
  },

  /** Subscribe; returns an unsubscribe fn. */
  subscribe(fn: Subscriber): () => void {
    _subscribers.add(fn);
    return () => {
      _subscribers.delete(fn);
    };
  },

  /** Test/diagnostics — reset both the snapshot and subscriber set. */
  _reset(): void {
    _state.clear();
    _subscribers.clear();
  },
};
